import { apiFetch } from "@/lib/api-fetch";
import { SUPABASE_URL } from "@/integrations/supabase/client";

const BASE = SUPABASE_URL.replace(/\/$/, "");

export type UploadKind = "linkedin_pdf"; // expandir conforme novos kinds

export interface PresignResponse {
  upload_url: string;
  fields: Record<string, string>;
  s3_key: string;
  expires_at: string;
}

export interface UploadOptions {
  kind: UploadKind;
  file: File;
  /** Callback chamado com o percentual 0-100 durante o upload pro S3. */
  onProgress?: (pct: number) => void;
}

export interface UploadResult {
  s3_key: string;
  original_filename: string;
}

/**
 * Calcula o SHA-256 (hex) do conteúdo do arquivo via Web Crypto.
 * Usado pra detectar uploads duplicados antes de processar.
 */
export async function computeFileHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Sobe um arquivo direto pro S3 via presigned POST. Não passa bytes pela API.
 *
 * Use o `s3_key` retornado pra confirmar no endpoint específico do recurso
 * (ex: POST /linkedin/jobs/confirm).
 *
 * Throws ApiError em falha de presign (autenticação, tipo inválido, tamanho >
 * permitido) ou Error em falha do POST S3 (rede, CORS, policy não bate).
 */
export async function uploadToS3({ kind, file, onProgress }: UploadOptions): Promise<UploadResult> {
  // 1. Pede presigned URL pra API
  const presign = await apiFetch<PresignResponse>(`${BASE}/uploads/presign`, {
    method: "POST",
    body: JSON.stringify({
      kind,
      content_type: file.type || "application/octet-stream",
      content_length: file.size,
      original_filename: file.name,
    }),
  });

  // 2. Sobe direto pro S3 via XHR (fetch não expõe upload progress)
  const formData = new FormData();
  for (const [k, v] of Object.entries(presign.fields)) {
    formData.append(k, v);
  }
  // S3 exige que o campo "file" seja o ÚLTIMO no FormData.
  formData.append("file", file);

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", presign.upload_url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 ${xhr.status}: ${xhr.responseText.slice(0, 500)}`));
      }
    };
    xhr.onerror = () => reject(new Error("Falha de rede no upload S3"));
    xhr.send(formData);
  });

  return { s3_key: presign.s3_key, original_filename: file.name };
}
